import { SECURITY_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { SECURITY_NOMINAL_VALUE_PRECISION } from '@dealstack/frontend/shared/cap';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Text, Title } from '@mantine/core';
import { memo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { NumberInput, Select } from 'react-hook-form-mantine';
import styled from 'styled-components';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import {
	CREATE_SECURITY_EVENT_FORM_FIELD,
	useSecurityEventDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityEventDrawer/useSecurityEventDrawerController.ts';
import { SecurityEventRes, SecurityEventType } from '@dealstack/shared/lib/generated';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';

interface Props {
	securityId?: string;
	entityId?: string;
	className?: string;
	opened: boolean;
	onClose: (createdTransactionId?: string) => void;
	refreshSecuritiesList: () => void;
	refreshSecurityNominalValue: () => void;
	securityEvent: SecurityEventRes;
}

const EditSecurityEventDrawer = ({
	securityId,
	entityId,
	opened,
	onClose,
	className,
	refreshSecuritiesList,
	refreshSecurityNominalValue,
	securityEvent,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		updateSecurityEventRequest,
		editSecurityEventForm,
		onEditSubmit,
		onDelete,
		availableSecurities,
		getConversionPlaceholder,
		handleSecurityTypeChange,
	} = useSecurityEventDrawerController(
		refreshSecuritiesList,
		refreshSecurityNominalValue,
		onClose,
		securityId,
		entityId,
		securityEvent
	);

	const selectedType = useWatch({
		control: editSecurityEventForm.control,
		name: CREATE_SECURITY_EVENT_FORM_FIELD.type,
	});

	const handleDelete = async () => {
		if (!securityEvent?.id) {
			return;
		}
		await onDelete(securityEvent.id);
		onClose();
	};

	const conversionPlaceholder = getConversionPlaceholder();

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('securities.details.edit_event')}
				</Title>
			}
		>
			{updateSecurityEventRequest.isPending && <InlineLoader />}
			<FormProvider {...editSecurityEventForm}>
				<form onSubmit={editSecurityEventForm.handleSubmit(onEditSubmit())}>
					<MantineDateInput
						name={CREATE_SECURITY_EVENT_FORM_FIELD.date}
						label={tCapManage('transactions.drawer.date.label')}
						size="md"
						onChange={(value) => {
							const isoDate = browserDateToMidnightUTC(value);
							if (!isoDate) {
								return;
							}
							editSecurityEventForm.setValue(
								CREATE_SECURITY_EVENT_FORM_FIELD.date,
								isoDate
							);
						}}
					/>
					<Select
						name={CREATE_SECURITY_EVENT_FORM_FIELD.type}
						control={editSecurityEventForm.control}
						label={tCapManage('securities.details.event_type')}
						placeholder={tCapManage('securities.drawer.type.placeholder')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={SECURITY_EVENT_TYPE_OPTIONS}
						onChange={(value: SecurityEventType) => {
							editSecurityEventForm.setValue(
								CREATE_SECURITY_EVENT_FORM_FIELD.type,
								value
							);
							handleSecurityTypeChange(value);
						}}
					/>
					{selectedType === SecurityEventType.SPLIT && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage('securities.details.split_one')}
							</span>
							<NumberInput
								name={CREATE_SECURITY_EVENT_FORM_FIELD.postEventUnits}
								control={editSecurityEventForm.control}
								size="md"
								hideControls={true}
								placeholder={tCapManage(
									'securities.drawer.votes_per_unit.placeholder'
								)}
								style={{ flexGrow: 1 }}
							/>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage('securities.details.split_unit')}
							</span>
						</div>
					)}

					{selectedType === SecurityEventType.REVERSE_SPLIT && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<NumberInput
								name={CREATE_SECURITY_EVENT_FORM_FIELD.preEventUnits}
								control={editSecurityEventForm.control}
								size="md"
								hideControls={true}
								placeholder={tCapManage(
									'securities.drawer.votes_per_unit.placeholder'
								)}
								style={{ flexGrow: 1 }}
							/>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage('securities.details.reverse_split')}
							</span>
						</div>
					)}

					{selectedType === SecurityEventType.ADJUST && (
						<NumberInput
							name={CREATE_SECURITY_EVENT_FORM_FIELD.securityDetailsNominalValue}
							control={editSecurityEventForm.control}
							size="md"
							hideControls={true}
							label={tCapManage('securities.details.adjust_value')}
							placeholder={tCapManage(
								'securities.drawer.new_nominal_value.placeholder'
							)}
							decimalScale={SECURITY_NOMINAL_VALUE_PRECISION}
							style={{ width: '100%' }}
						/>
					)}

					{selectedType === SecurityEventType.CONVERSION && (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<Text size="B6Bold">{tCapManage('securities.details.conversion')}</Text>
							<Select
								name={CREATE_SECURITY_EVENT_FORM_FIELD.postEventSecurityId}
								control={editSecurityEventForm.control}
								size="md"
								rightSection={<Icon variant="caret-down" size="lg" />}
								rightSectionProps={{ style: { pointerEvents: 'none' } }}
								data={availableSecurities}
								placeholder={conversionPlaceholder}
								disabled={availableSecurities.length === 0}
							/>
						</div>
					)}

					<Button fullWidth mt="auto" type="submit">
						{tCapManage('securities.details.button_edit_event')}
					</Button>
					<Button variant="subtle" color="red" onClick={handleDelete}>
						{tCapManage('securities.details.button_delete_event')}
					</Button>
				</form>
			</FormProvider>
		</Drawer>
	);
};

const Memoized = memo(EditSecurityEventDrawer);
const Styled = styled(Memoized)`
	form {
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export { Styled as EditSecurityEventDrawer };
